<template>
  <section class="hero-details">
    <header class="widget-header">
      <h2>Hero Details</h2>
      <p class="kicker">Active champion at a glance</p>
    </header>

    <div v-if="!hasHero" class="empty-block">
      <p class="empty-copy">
        Recruit a hero to load identity, combat totals, and equipped gear.
      </p>
      <router-link class="cta-link" to="/game/hero">Open Hero Map</router-link>
    </div>

    <template v-else>
      <HeroTagChip
        block
        :name="sheet.identity.name"
        :meta="identityMeta"
        :image="identityCard?.image || ''"
        :image-position="identityCard?.image_position"
        :rarity="identityCard?.rarity || 'common'"
      />

      <section class="stat-plaque grand-total">
        <h3>Grand Total</h3>
        <p v-if="!identityCard" class="empty-copy">
          Socket a hero card on the Hero node to load base stats and abilities.
        </p>
        <div v-else class="total-display">
          <div
            v-for="field in combatFields"
            :key="'total-' + field.key"
            class="total-cell"
          >
            <span class="total-label">{{ field.label }}</span>
            <span class="total-value">{{
              formatCombatValue(field.key, sheet.total.stats[field.key])
            }}</span>
          </div>
        </div>
      </section>

      <section class="stat-plaque">
        <h3>Equipped Gear</h3>
        <p v-if="!sheet.equipment.pieces.length" class="empty-copy">
          No equipment socketed.
        </p>
        <div v-else class="piece-list">
          <HeroTagChip
            v-for="piece in sheet.equipment.pieces"
            :key="piece.nodeKey"
            block
            :name="piece.card?.name || piece.label"
            :meta="equipmentMeta(piece)"
            :image="piece.card?.image || ''"
            :image-position="piece.card?.image_position"
            :rarity="piece.card?.rarity || 'common'"
          />
        </div>
      </section>
    </template>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { useGameStore } from "../store/game";
import HeroTagChip from "./HeroTagChip.vue";
import {
  COMBAT_STAT_FIELDS,
  compactStatSummary,
  formatCombatValue,
  titleCaseStatKey,
} from "../utils/heroCombatStats";

const gameStore = useGameStore();
const combatFields = COMBAT_STAT_FIELDS;

const hasHero = computed(() => Boolean(gameStore.activeHero));
const sheet = computed(() => gameStore.activeHeroCombatSheet);
const identityCard = computed(() => sheet.value.identity.card);

const identityMeta = computed(() => {
  const { level, experience } = sheet.value.identity;
  return `Hero · Level ${level || 1} · XP ${experience || 0}`;
});

function extrasSummary(extras) {
  return Object.entries(extras || {})
    .filter(([, amount]) => Number(amount) !== 0)
    .map(([key, amount]) => {
      const n = Number(amount) || 0;
      const sign = n > 0 ? "+" : "";
      return `${sign}${n} ${titleCaseStatKey(key)}`;
    })
    .join(" · ");
}

function equipmentMeta(piece) {
  const slot = titleCaseStatKey(piece.slot);
  const summary = compactStatSummary(piece.stats);
  const extras = extrasSummary(piece.extras);
  return [slot, summary, extras].filter(Boolean).join(" · ");
}
</script>

<style scoped>
.hero-details {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  height: 100%;
  min-height: 0;
  overflow: auto;
  padding: 0.9rem 1rem 1rem;
  box-sizing: border-box;
  color: #e7ecf1;
}

.widget-header h2 {
  margin: 0;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9b48a;
}

.kicker {
  margin: 0.15rem 0 0;
  font-size: 0.72rem;
  color: #88c0d0;
}

.empty-block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.empty-copy {
  margin: 0;
  color: #8892b0;
  font-size: 0.8rem;
  font-style: italic;
}

.cta-link {
  align-self: flex-start;
  color: #00f0ff;
  border: 1px solid #00f0ff;
  border-radius: 4px;
  padding: 0.4rem 0.7rem;
  text-decoration: none;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.cta-link:hover {
  background: rgba(0, 240, 255, 0.12);
}

.stat-plaque {
  background: rgba(10, 15, 30, 0.95);
  border: 1px solid #00f0ff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 240, 255, 0.1);
  padding: 0.95rem 1rem 1.1rem;
}

.stat-plaque h3 {
  margin: 0 0 0.75rem;
  font-family: "Cinzel", serif;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9b48a;
}

.stat-plaque.grand-total {
  background: rgba(0, 5, 15, 0.85);
  box-shadow: 0 0 14px rgba(0, 240, 255, 0.18);
}

.total-display {
  display: flex;
  gap: 0.55rem;
  padding: 0.65rem 0.5rem;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid #333;
}

.total-cell {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
}

.total-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #888;
  overflow-wrap: anywhere;
  text-align: center;
}

.total-value {
  font-size: 1.15rem;
  font-weight: 800;
  color: #00f0ff;
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.35);
  overflow-wrap: anywhere;
  text-align: center;
}

@media (max-width: 768px) {
  .total-display {
    gap: 4px;
    padding: 0.5rem 0.25rem;
  }

  .total-label {
    font-size: 0.58rem;
    letter-spacing: 0.04em;
  }

  .total-value {
    font-size: 0.95rem;
  }
}

.piece-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}
</style>
